import React, { useState, createContext, useMemo, useContext, ReactNode } from 'react'
import { Content, mapContent, MappedContent } from '@/utils/TorchGuildUtil'
import { Tab } from '../Details/Details'
import { Dynamic } from '../Dynamic'

interface DetailsProviderProps {
  children: ReactNode
  content: Content[]
}

interface Details {
  tabs: Tab[]
  selectedTab: string
  setTab: React.Dispatch<React.SetStateAction<string>>
}

const DetailsContext = createContext<Partial<Details>>({})

export const useDetailsContext = () => useContext(DetailsContext)

export const DetailsProvider: React.FC<DetailsProviderProps> = ({ children, content }) => {
  // title is required for tabs, but not required in Contentful
  const [tab, setTab] = useState(content[0].sys.id)

  const tabs = useMemo(() => {
    const tabContent = content.filter((c) => !!c.fields.title)
    const allTabs = tabContent.map((c: Content): Tab => {
      const componentContent = mapContent(c) as MappedContent

      const visible = !!componentContent

      return {
        contentType: `${componentContent.contentType}${componentContent?.layout || ''}`,
        id: `${componentContent.contentType}-${String(componentContent.title).toLowerCase().replace(/\s/g, '-')}`,
        sysId: componentContent.id,
        linkText: componentContent.linkText as string,
        title: String(componentContent.title),
        visible,
        Component() {
          return <Dynamic key={componentContent.id} content={componentContent} />
        },
      }
    })

    return allTabs.filter(
      ({ contentType, visible }) => !visible || (!contentType.match(/offering/i) && !contentType.match(/perks/i)),
    )
  }, [content])

  return (
    <DetailsContext.Provider
      value={{
        selectedTab: tab,
        setTab,
        tabs,
      }}
    >
      {children}
    </DetailsContext.Provider>
  )
}
